import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Menu from '../../components/menu/menu';
import Content from '../../components/content/content';
import DashboardCard from '../../components/components/dashboard_card/DashboardCard';
import ActivityItem from '../../components/components/activity_item/ActivityItem';
import './Dashboard.css';

import { ReactComponent as Ticket } from '../../components/icons/ticket.svg';
import { ReactComponent as PersonalCard } from '../../components/icons/personalcard.svg';
import { ReactComponent as UserEdit } from '../../components/icons/user-edit.svg';
import { ReactComponent as Danger } from '../../components/icons/danger.svg';

import ApiService from '../../components/api_services/apiService';
import {
  isPendingDeactivation,
  isPendingActivation,
  parsePendingPayload,
} from '../../utils/eventStatus';
import {
  setAddEventMode,
  setEditedEvent,
  openEditModal,
  clearPendingEditReview,
  setPendingEditReview,
  setModalViewActualTab,
} from '../../store/eventsSlice';

function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    pendingEvents: 4,
    newOrganizers: 1,
    pendingDataChanges: 4,
    newReports: 0,
    newMessageReports: 0,
  });

  const [activityData, setActivityData] = useState([
    { id: 1, type: 'event', dateTime: '12.08.2025 10:30', title: 'Festiwal 1, Warszawa' },
    { id: 2, type: 'event', dateTime: '11.08.2025 12:22', title: 'Festiwal Kolor\u00f3w, Warszawa' },
    { id: 3, type: 'event', dateTime: '11.08.2025 12:00', title: 'Pokazy sztuk mistrz\u00f3w, Warszawa' },
    { id: 4, type: 'event', dateTime: '10.08.2025 09:10', title: 'Festiwal 1' },
    { id: 5, type: 'organizer', dateTime: '12.08.2025 12:45', title: 'Dom kultury Praga P\u00f3\u0142noc' },
    { id: 6, type: 'dataChange', dateTime: '12.08.2025 12:45', title: 'Dom kultury Praga P\u00f3\u0142noc' },
    { id: 7, type: 'dataChange', dateTime: '12.08.2025 12:45', title: 'Dom kultury Praga P\u00f3\u0142noc' },
    { id: 8, type: 'dataChange', dateTime: '12.08.2025 12:45', title: 'Dom kultury Praga P\u00f3\u0142noc' },
    { id: 9, type: 'dataChange', dateTime: '12.08.2025 12:45', title: 'Dom kultury Praga P\u00f3\u0142noc' },
  ]);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const normalizeListFromResponse = (response) => {
    const payload = response?.data ?? response;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.content)) return payload.content;
    return [];
  };

  const getTotalCountFromResponse = (response, fallbackLength) => {
    const payload = response?.data ?? response;
    const candidates = [
      payload?.totalCount,
      payload?.totalElements,
      payload?.count,
      payload?.allSize,
      response?.totalCount,
      response?.totalElements,
      response?.count,
      response?.allSize,
    ];

    const resolved = candidates.find((value) => Number.isFinite(Number(value)));
    return resolved !== undefined ? Number(resolved) : fallbackLength;
  };

  const toSortTimestamp = (rawDate) => {
    if (!rawDate) {
      return 0;
    }
    const parsed = Date.parse(rawDate);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const sortNewestFirst = (items) =>
    [...items].sort((a, b) => (b.sortTimestamp || 0) - (a.sortTimestamp || 0));

  const getProfileDisplayName = (profile) => {
    if (!profile) return '';

    const nickname = String(profile?.nick || '').trim();
    const fullName = [profile?.name, profile?.surname]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join(' ');

    if (nickname && fullName) return `${nickname} (${fullName})`;
    return nickname || fullName;
  };

  const getReportUserDisplayName = (user) => {
    if (!user) return '';
    const nickname = String(user.nickname || user.nick || '').trim();
    const displayName = String(user.displayName || user.fullName || '').trim();
    if (nickname && displayName) return `${nickname} (${displayName})`;
    return nickname || displayName;
  };

  const fetchProfilesByIds = async (ids) => {
    const uniqueIds = [...new Set(ids.filter(Boolean))];
    const results = await Promise.allSettled(
      uniqueIds.map(async (id) => ({
        id,
        profile: await ApiService.getProfileById(id),
      }))
    );

    return new Map(
      results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => [result.value.id, result.value.profile])
    );
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [
        eventsResponse,
        organizersResponse,
        pendingEditsResponse,
        reportsResponse,
        messageReportsResponse,
        pendingEventEditsResponse,
      ] = await Promise.allSettled([
        ApiService.getEvents2(),
        ApiService.getOnlyNotApprovedOrganisers(),
        ApiService.getEvents(null, null, 1, null, 'PENDING_EDITS', 10),
        ApiService.getUserReports(),
        ApiService.getMessageReports(),
        ApiService.getPendingEventEdits(),
      ]);

      const eventsResult = eventsResponse.status === 'fulfilled' ? eventsResponse.value : null;
      const organizersResult = organizersResponse.status === 'fulfilled' ? organizersResponse.value : null;
      const pendingEditsResult = pendingEditsResponse.status === 'fulfilled' ? pendingEditsResponse.value : null;
      const reportsResult = reportsResponse.status === 'fulfilled' ? reportsResponse.value : null;
      const messageReportsResult = messageReportsResponse.status === 'fulfilled' ? messageReportsResponse.value : null;
      const pendingEventEditsResult = pendingEventEditsResponse.status === 'fulfilled' ? pendingEventEditsResponse.value : null;

      const events = normalizeListFromResponse(eventsResult);
      const organizers = normalizeListFromResponse(organizersResult);
      const pendingEdits = normalizeListFromResponse(pendingEditsResult);
      const reports = normalizeListFromResponse(reportsResult);
      const messageReports = Array.isArray(messageReportsResult?.items)
        ? messageReportsResult.items
        : normalizeListFromResponse(messageReportsResult);

      const pendingEditsList = Array.isArray(pendingEventEditsResult)
        ? pendingEventEditsResult
        : normalizeListFromResponse(pendingEventEditsResult);
      const pendingEditsMap = new Map(
        pendingEditsList.map((entry) => [String(entry?.eventId ?? entry?.id), entry])
      );

      const reportProfileIds = [
        ...reports.flatMap((report) => [report?.reportedUserId, report?.reportSenderId]),
      ];
      const profilesById = await fetchProfilesByIds(reportProfileIds);

      const eventCount = getTotalCountFromResponse(eventsResult, events.length);
      const organizersCount = getTotalCountFromResponse(organizersResult, organizers.length);
      const pendingEditsCount = getTotalCountFromResponse(pendingEditsResult, pendingEdits.length);
      const reportsCount = getTotalCountFromResponse(reportsResult, reports.length);
      const messageReportsCount = messageReportsResult?.summary?.totalUnresolvedReports
        ?? messageReportsResult?.pagination?.totalItems
        ?? messageReports.length;

      const eventActivities = sortNewestFirst(events.map((event, index) => {
        const rawDate = event.added_on_utc || event.startDate;
        return {
          id: `event-${event.id || index}`,
          eventId: event.id,
          type: 'event',
          dateTime: formatDateTime(rawDate),
          sortTimestamp: toSortTimestamp(rawDate),
          title: `${event.name}${event.city ? `, ${event.city}` : ''}`,
        };
      }));

      const organizerActivities = sortNewestFirst(organizers.map((organizer, index) => {
        const organizerId = organizer?.id ?? organizer?.organiserProfileId ?? organizer?.organizerProfileId ?? index;
        const organizerName = organizer?.companyName || organizer?.name || organizer?.fullName || `Organizator #${organizerId}`;
        const rawDate =
          organizer?.createdAt ||
          organizer?.addedOnUtc ||
          organizer?.added_on_utc ||
          organizer?.registrationDate ||
          organizer?.created_on_utc;

        return {
          id: `org-${organizerId}`,
          organizerId,
          type: 'organizer',
          dateTime: rawDate ? formatDateTime(rawDate) : '',
          sortTimestamp: toSortTimestamp(rawDate),
          title: organizerName,
        };
      }));

      const reportActivities = sortNewestFirst(reports.map((report, index) => {
        const reportId = report?.reportId ?? index;
        const rawDate = report?.createdAt || report?.created_at || report?.addedOnUtc;
        const reportedProfileName = getProfileDisplayName(profilesById.get(report?.reportedUserId));
        return {
          id: `rep-${reportId}`,
          reportId,
          type: 'report',
          dateTime: rawDate ? formatDateTime(rawDate) : '',
          sortTimestamp: toSortTimestamp(rawDate),
          title: reportedProfileName || report?.reason || `Zgloszenie #${reportId}`,
        };
      }));

      const messageReportActivities = sortNewestFirst(messageReports.map((report, index) => {
        const reportId = report?.reportGroupId || report?.groupKey || report?.reportId || index;
        const rawDate = report?.lastReportedAt || report?.createdAt || report?.messages?.[0]?.message?.createdAt;
        const senderProfileName = getReportUserDisplayName(report?.reportSender);
        return {
          id: `message-rep-${reportId}`,
          reportId,
          reportGroupId: report?.reportGroupId || report?.groupKey,
          report,
          type: 'messageReport',
          dateTime: rawDate ? formatDateTime(rawDate) : '',
          sortTimestamp: toSortTimestamp(rawDate),
          title: senderProfileName || report?.reason || `Zgłoszenie wiadomości #${reportId}`,
        };
      }));

      const dataChangeActivities = sortNewestFirst(pendingEdits.map((item, index) => {
        const eventId = item?.id ?? index;
        const eventName = item?.name || `Wydarzenie #${eventId ?? '-'}`;
        const requestedAt = item?.pendingEditRequestedAt ?? item?.pendingRequestedAt;
        const matchingPendingItem = pendingEditsMap.get(String(eventId)) || null;
        const isDeactivation = isPendingDeactivation(matchingPendingItem || item, item);
        const isActivation = isPendingActivation(matchingPendingItem || item, item);

        return {
          id: `dc-${eventId}`,
          eventId,
          type: 'dataChange',
          isDeactivation,
          isActivation,
          badgeText: isDeactivation ? 'Dezaktywacja' : isActivation ? 'Aktywacja' : null,
          badgeType: isDeactivation ? 'danger' : isActivation ? 'success' : 'dataChange',
          subtitle: isDeactivation
            ? 'Prośba o dezaktywację wydarzenia'
            : isActivation
            ? 'Prośba o aktywację wydarzenia'
            : null,
          dateTime: formatDateTime(requestedAt),
          sortTimestamp: toSortTimestamp(requestedAt),
          title: eventName,
          pendingItem: matchingPendingItem,
        };
      }));

      setActivityData([
        ...eventActivities,
        ...organizerActivities,
        ...dataChangeActivities,
        ...reportActivities,
        ...messageReportActivities,
      ]);

      setDashboardData((prev) => ({
        ...prev,
        pendingEvents: eventCount,
        newOrganizers: organizersCount,
        pendingDataChanges: pendingEditsCount,
        newReports: reportsCount,
        newMessageReports: messageReportsCount,
      }));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShowAll = (type) => {
    switch (type) {
      case 'events':
        navigate('/events');
        break;
      case 'organizers':
        navigate('/organizers');
        break;
      case 'dataChanges':
        navigate('/events', { state: { filterQuery: 'PENDING_EDITS' } });
        break;
      case 'reports':
        navigate('/reports');
        break;
      case 'messageReports':
        navigate('/users', { state: { pageTab: 'messageReports' } });
        break;
      default:
        break;
    }
  };

  const handleActivityClick = async (item) => {
    switch (item.type) {
      case 'event': {
        const eventId = Number(item?.eventId ?? String(item?.id || '').replace('event-', ''));
        if (Number.isFinite(eventId)) {
          dispatch(setAddEventMode(false));
          dispatch(clearPendingEditReview());
          dispatch(setModalViewActualTab('Dane podstawowe'));
          dispatch(setEditedEvent(eventId));
          dispatch(openEditModal());
        }
        navigate('/events');
        break;
      }
      case 'organizer':
        navigate('/organizers');
        break;
      case 'dataChange':
        if (Number.isFinite(Number(item?.eventId))) {
          const eventId = Number(item.eventId);
          let pendingItem = item?.pendingItem || null;

          if (!pendingItem) {
            try {
              const pendingEdits = await ApiService.getPendingEventEdits();
              const list = Array.isArray(pendingEdits) ? pendingEdits : [];
              pendingItem = list.find((entry) => String(entry?.eventId) === String(eventId)) || null;
            } catch (error) {
              console.error('Error fetching pending edit details:', error);
            }
          }

          const parsedPayload = parsePendingPayload(pendingItem?.pendingChange?.payloadJson);
          const isDeactivation = Boolean(item?.isDeactivation || isPendingDeactivation(pendingItem || parsedPayload));
          const isActivation = Boolean(item?.isActivation || isPendingActivation(pendingItem || parsedPayload));

          dispatch(setAddEventMode(false));
          dispatch(setModalViewActualTab('Dane podstawowe'));
          dispatch(setEditedEvent(eventId));
          dispatch(clearPendingEditReview());
          dispatch(setPendingEditReview({
            eventId,
            pendingId: pendingItem?.id ?? pendingItem?.eventId ?? eventId,
            payload: parsedPayload,
            isDeactivation,
            isActivation,
            requestedAt: pendingItem?.pendingChange?.requestedAt ?? null,
          }));
          dispatch(openEditModal());
        }
        navigate('/events', { state: { filterQuery: 'PENDING_EDITS' } });
        break;
      case 'report':
        navigate('/reports');
        break;
      case 'messageReport':
        navigate('/users', {
          state: {
            pageTab: 'messageReports',
            messageReportPreview: item.report,
          },
        });
        break;
      default:
        break;
    }
  };

  const groupedActivities = {
    events: activityData.filter((a) => a.type === 'event'),
    organizers: activityData.filter((a) => a.type === 'organizer'),
    dataChanges: activityData.filter((a) => a.type === 'dataChange'),
    reports: activityData.filter((a) => a.type === 'report'),
    messageReports: activityData.filter((a) => a.type === 'messageReport'),
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <Menu />
        <Content>
          <div className="dashboard-loading">Ladowanie...</div>
        </Content>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <Menu />
        <Content>
        <div className="dashboard-container">
          <div className="dashboard-horizontal-scroll">
            <div className="dashboard-horizontal-content">
            <div className="dashboard-cards-row">
            <DashboardCard
              title="wydarzenia"
              count={dashboardData.pendingEvents}
              description="do zaakceptowania"
              backgroundColor="#EE8F3B"
              icon={Ticket}
              onShowAll={() => handleShowAll('events')}
            />
            <DashboardCard
              title="organizatorzy"
              count={dashboardData.newOrganizers}
              description="profile"
              backgroundColor="#7D959F"
              icon={PersonalCard}
              onShowAll={() => handleShowAll('organizers')}
            />
            <DashboardCard
              title="Zmiana danych"
              count={dashboardData.pendingDataChanges}
              description="do zaakceptowania"
              backgroundColor="#201E25"
              icon={UserEdit}
              onShowAll={() => handleShowAll('dataChanges')}
            />
            <DashboardCard
              title="zgloszenia profili"
              count={dashboardData.newReports}
              description="do rozpatrzenia"
              backgroundColor="#DD2626"
              icon={Danger}
              onShowAll={() => handleShowAll('reports')}
              actionLabel="Zgłoszenia profili"
            />
            <DashboardCard
              title="zgloszenia wiadomości"
              count={dashboardData.newMessageReports}
              description="do rozpatrzenia"
              backgroundColor="#8B1E3F"
              icon={Danger}
              onShowAll={() => handleShowAll('messageReports')}
              actionLabel="Zgłoszenia wiadomości"
            />
            </div>

            <div className="dashboard-activity-section">
            <div className="dashboard-activity-column">
              <div className="dashboard-activity-header">
                <div className="dashboard-activity-indicator" style={{ backgroundColor: '#EE8F3B' }} />
                <span>Wydarzenia do akceptacji</span>
              </div>
              <div className="dashboard-activity-list">
                {groupedActivities.events.map((item) => (
                  <ActivityItem
                    key={item.id}
                    type={item.type}
                    dateTime={item.dateTime}
                    title={item.title}
                    onClick={() => handleActivityClick(item)}
                  />
                ))}
              </div>
            </div>

            <div className="dashboard-activity-column">
              <div className="dashboard-activity-header">
                <div className="dashboard-activity-indicator" style={{ backgroundColor: '#7D959F' }} />
                <span>Nowi organizatorzy</span>
              </div>
              <div className="dashboard-activity-list">
                {groupedActivities.organizers.map((item) => (
                  <ActivityItem
                    key={item.id}
                    type={item.type}
                    dateTime={item.dateTime}
                    title={item.title}
                    onClick={() => handleActivityClick(item)}
                  />
                ))}
              </div>
            </div>

            <div className="dashboard-activity-column">
              <div className="dashboard-activity-header">
                <div className="dashboard-activity-indicator" style={{ backgroundColor: '#201E25' }} />
                <span>Zmiany danych do akceptacji</span>
              </div>
              <div className="dashboard-activity-list">
                {groupedActivities.dataChanges.map((item) => (
                  <ActivityItem
                    key={item.id}
                    type={item.type}
                    dateTime={item.dateTime}
                    title={item.title}
                    subtitle={item.subtitle}
                    badgeText={item.badgeText}
                    badgeType={item.badgeType}
                    isDeactivation={item.isDeactivation}
                    isActivation={item.isActivation}
                    onClick={() => handleActivityClick(item)}
                  />
                ))}
              </div>
            </div>

            <div className="dashboard-activity-column">
              <div className="dashboard-activity-header">
                <div className="dashboard-activity-indicator" style={{ backgroundColor: '#DD2626' }} />
                <span>Nowe zgloszenia profili</span>
              </div>
              <div className="dashboard-activity-list">
                {groupedActivities.reports.map((item) => (
                  <ActivityItem
                    key={item.id}
                    type={item.type}
                    dateTime={item.dateTime}
                    title={item.title}
                    onClick={() => handleActivityClick(item)}
                  />
                ))}
              </div>
            </div>

            <div className="dashboard-activity-column">
              <div className="dashboard-activity-header">
                <div className="dashboard-activity-indicator" style={{ backgroundColor: '#8B1E3F' }} />
                <span>Nowe zgloszenia wiadomości</span>
              </div>
              <div className="dashboard-activity-list">
                {groupedActivities.messageReports.map((item) => (
                  <ActivityItem
                    key={item.id}
                    type={item.type}
                    dateTime={item.dateTime}
                    title={item.title}
                    onClick={() => handleActivityClick(item)}
                  />
                ))}
              </div>
            </div>
            </div>
            </div>
          </div>
        </div>
      </Content>
    </div>
  );
}

export default Dashboard;
